import React, { useEffect, useRef, useState } from "react"
import RetryPanel, { isNetworkError } from "../ui/RetryPanel"
import PubSub from "pubsub-js"
import { TruckIcon, BuildingStorefrontIcon, ArchiveBoxIcon, MapPinIcon, ChevronLeftIcon, CursorArrowRaysIcon, ShoppingBagIcon } from "@heroicons/react/24/outline"
import { CheckCircleIcon } from "@heroicons/react/24/solid"
import Button from "aldoo-ra/Button"
import useT from "../hooks/useT"
import useSiteLanguage from "../hooks/useSiteLanguage"
import Spinner from "../ui/Spinner"
import PriceDisplay from "../ui/PriceDisplay"
import { ShippingOptions, ShippingOffices, UserAddresses, isSignedIn } from "../storeApi"
import { formatMoney } from "../account/ui"
import { expectedDelivery } from "../utils/delivery"
import { cartSubtotal, hasPhysicalItems, lineProduct, maxDeliveryDays, presaleLines, toCheckoutCart } from "./cartItems"
import { isPhysicalProduct } from "../utils/productUtils"
import { presaleDate } from "../utils/presale"
import { useCurrency } from "../store/currency"
import OfficePicker from "./shipping/OfficePicker"
import DeliveryUpsell from "./DeliveryUpsell"
import { setValue } from "aldoo-ra/Value"
import { PAY_AT_SHOP_KEY } from "./payment"
import { FreeDeliveryChip } from "../ui/cart/CartUpsell"
import { ga4Item, track } from "../ga4"
import { officeAddress } from "./shipping/providers"
import { url as cmsUrl } from "aldoo-ra/CMS/url"

const METHOD_ICONS = { address: TruckIcon, office: BuildingStorefrontIcon, locker: ArchiveBoxIcon, pickup: ShoppingBagIcon }

// the way the last order went: kept in this browser, and on the account of a
// signed-in buyer (the API saves it at payment); the newer of the two wins
const LAST_DELIVERY_KEY = "sf-last-delivery"
const readLocal = () => {
  try {
    return JSON.parse(localStorage.getItem(LAST_DELIVERY_KEY) || "null")
  } catch {
    return null
  }
}
export const saveLocal = (value) => {
  try {
    localStorage.setItem(LAST_DELIVERY_KEY, JSON.stringify({ ...value, at: new Date().toISOString() }))
  } catch {}
}

// the shop picked last at each seller (pickup from the shop): offered again, still changeable
const LAST_SHOPS_KEY = "sf-last-shops"
const readShops = () => {
  try {
    return JSON.parse(localStorage.getItem(LAST_SHOPS_KEY) || "{}") || {}
  } catch {
    return {}
  }
}
export const rememberShop = (sellerId, office) => {
  if (!sellerId || !office?.id) return
  try {
    localStorage.setItem(LAST_SHOPS_KEY, JSON.stringify({ ...readShops(), [sellerId]: { id: office.id, at: new Date().toISOString() } }))
  } catch {}
}
/** The shop last picked at a seller, checked to be still open for pickups, or null. */
async function rememberedShop(option, sellerId) {
  const last = sellerId ? readShops()[sellerId] : null
  if (!last?.id) return null
  const found = await ShippingOffices({ providerId: option.providerId, method: option.method, id: last.id, sellerId }).catch(() => null)
  return found?.office || null
}

/** `{ key, office }` of the last delivery, its office checked to be still open, or null. */
async function rememberedDelivery() {
  const local = readLocal()
  const account = isSignedIn() ? (await UserAddresses().catch(() => null))?.lastDelivery : null
  const last = [local, account].filter((entry) => entry?.providerId && entry?.method).sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0))[0]
  if (!last) return null
  const key = `${last.providerId}:${last.method}`
  if (!last.office?.id) return { key, office: null }
  // a seller's own shop: its seller is not known here — the way is kept, the shop picked again
  if (last.providerId === "shop") return { key, office: null }
  const found = await ShippingOffices({ providerId: last.providerId, method: last.method, id: last.office.id }).catch(() => null)
  return { key, office: found?.office || null }
}


/**
 * Checkout step "delivery": how the parcels reach the buyer. The options are
 * the provider methods every seller of the cart offers (store
 * {shipping_options}: to the address, to a Speedy office, to a Speedy
 * locker…), each priced for the whole cart. A pickup method needs its office
 * or locker picked (OfficePicker) and is priced again for it. The cost is
 * added to the cart total as an extra charge; the choice goes on to the
 * payment, which prices it once more on the server.
 *
 * One seller: its options, nothing about parcels. Several sellers sharing a courier: the common
 * options, each with its parcels by seller. Sellers with no courier in common: each parcel is
 * delivered its own way — the step lists the sellers, and each is set up as a one-seller checkout
 * (its options, its office), coming back to the list with a tick; once all are set, it continues.
 */
export default function DeliveryStep({ store, isActive, controller, stepData }) {
  const t = useT()
  const cart = store.cart
  const physical = hasPhysicalItems(cart)
  const address = controller.getData("billing")?.address
  const country = address?.country || "BG"
  const language = useSiteLanguage()
  const [options, setOptions] = useState([])
  const [selectedKey, setSelectedKey] = useState(stepData?.option?.key || null)
  // the office picked for each pickup option, kept when the buyer switches back and forth
  // (kept with the step's data, so leaving the step and coming back keeps every
  // one of them, not only the option that was selected)
  const [offices, setOffices] = useState(() => ({ ...(stepData?.offices || {}), ...(stepData?.option?.office ? { [stepData.option.key]: stepData.option.office } : {}) }))
  const [error, setError] = useState(null)
  // "Try again" after a failed load: asked once more (RetryPanel)
  const [attempt, setAttempt] = useState(0)
  // sellers whose products share no delivery method (a product may allow only
  // some of its company's): the cart cannot go as it is
  const [problems, setProblems] = useState([])
  const [pending, setPending] = useState(physical)
  const [pricing, setPricing] = useState(false)
  const [picker, setPicker] = useState(false)
  // sellers with no courier in common: [{ sellerId, sellerName, options }], each set up on its own
  const [perSeller, setPerSeller] = useState(null)
  // the way set for each of them: sellerId → { key, office }
  const [choices, setChoices] = useState(() => Object.fromEntries((stepData?.deliveries || []).map((entry) => [entry.sellerId, { key: entry.option?.key, office: entry.option?.office || null }])))
  // the offices picked for each seller's options, kept while switching between them
  const [sellerOffices, setSellerOffices] = useState(() => Object.fromEntries((stepData?.deliveries || []).filter((entry) => entry.option?.office).map((entry) => [entry.sellerId, { [entry.option.key]: entry.option.office }])))
  // the seller being set up (its options shown as a one-seller checkout), or null: the list of sellers
  const [editing, setEditing] = useState(null)
  const continueRef = useRef(null)
  const cartKey = toCheckoutCart(cart)
    .map(({ pid, quantity }) => `${pid}:${quantity}`)
    .join(",")
  // `language`: each option's courier (`carrier`: name, logo, a line about it) comes from the CMS in it
  const request = () => ({ cart: toCheckoutCart(cart).map(({ pid, quantity }) => ({ pid, quantity })), countryCode: country, city: address?.city, postCode: address?.postal_code, language })

  useEffect(() => {
    if (!physical) return
    let alive = true
    setPending(true)
    setError(null)
    setProblems([])
    ;(async () => {
      // nothing chosen yet in this checkout: start from the last order's way
      let preferred = stepData?.option?.key || null
      let known = offices
      const last = preferred ? null : await rememberedDelivery().catch(() => null)
      if (last?.key) {
        preferred = last.key
        if (last.office) known = { ...known, [last.key]: last.office }
      }
      if (!alive) return null
      if (known !== offices) setOffices(known)
      const result = await ShippingOptions(request())
      return { result, preferred, known }
    })()
      .then(async (loaded) => {
        if (!alive || !loaded) return
        const { result, preferred } = loaded
        let { known } = loaded
        if (!result?.options) return setError(!result || isNetworkError(result?.error) ? { network: true } : t(result?.error || "Delivery could not be priced. Try again in a moment."))
        // no courier in common: each seller's options, and the ways set before kept (their offices priced again)
        if (Array.isArray(result.perSeller) && result.perSeller.length) {
          const sellers = []
          for (const seller of result.perSeller) {
            let sellerOptions = seller.options || []
            const choice = choices[seller.sellerId]
            if (choice?.office && choice.key) {
              const priced = await ShippingOptions({ ...request(), key: choice.key, officeId: choice.office.id, sellerId: seller.sellerId }).catch(() => null)
              if (priced?.options?.[0]) sellerOptions = sellerOptions.map((option) => (option.key === choice.key ? priced.options[0] : option))
            }
            sellers.push({ ...seller, options: sellerOptions })
            // a shop picked here before: selected, ready, when this seller's setup is opened
            const shopOption = sellerOptions.find((option) => option.officeType === "shop" && !option.unavailable)
            if (shopOption && !choice) {
              const shop = await rememberedShop(shopOption, seller.sellerId)
              if (shop && alive) setSellerOffices((current) => (current[seller.sellerId] ? current : { ...current, [seller.sellerId]: { [shopOption.key]: shop } }))
            }
          }
          if (!alive) return
          setChoices((current) => Object.fromEntries(Object.entries(current).filter(([sellerId, choice]) => sellers.some((seller) => seller.sellerId === sellerId && seller.options.some((option) => option.key === choice.key && !option.unavailable)))))
          setPerSeller(sellers)
          setOptions([])
          setProblems([])
          // the address step is not one here (asked with the sellers): out of the step header at once
          if (!stepData?.perSeller) PubSub.publish("PURCHASE_FLOW_SET_DATA", { stepId: "delivery", data: { ...(stepData || {}), perSeller: true } })
          return
        }
        setPerSeller(null)
        let list = result.options
        // the shop picked last at this seller: offered again (the buyer may change it)
        const shopOption = list.find((option) => option.officeType === "shop" && !option.unavailable)
        if (shopOption && !known[shopOption.key]) {
          const shop = await rememberedShop(shopOption, shopOption.sellerShipping?.[0]?.sellerId)
          if (shop) {
            known = { ...known, [shopOption.key]: shop }
            setOffices(known)
          }
        }
        // an office picked before is priced for it again
        for (const [key, office] of Object.entries(known)) {
          const priced = await ShippingOptions({ ...request(), key, officeId: office.id }).catch(() => null)
          if (priced?.options?.[0]) list = list.map((option) => (option.key === key ? priced.options[0] : option))
        }
        if (!alive) return
        setOptions(list)
        setProblems(Array.isArray(result.problems) ? result.problems : [])
        // selected for the buyer only when ready to go: a pickup needs its office or locker picked by them
        // (selecting it themselves opens the picker), so one without is left unselected
        const ready = (option) => !option.unavailable && (!option.pickup || !!known[option.key])
        const usable = (key) => list.some((option) => option.key === key && ready(option))
        setSelectedKey((key) => (usable(key) ? key : usable(preferred) ? preferred : list.find(ready)?.key || null))
      })
      .catch((e) => alive && setError(isNetworkError(e) ? { network: true } : e.message))
      .finally(() => alive && setPending(false))
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, physical, cartKey, address?.city, address?.postal_code, attempt])

  const selected = options.find((option) => option.key === selectedKey) || null
  // the free-delivery products at the foot: whether there are any, and whether they are in view
  const upsellRef = useRef(null)
  const [upsellShown, setUpsellShown] = useState(false)
  const [upsellInView, setUpsellInView] = useState(true)
  useEffect(() => {
    const el = upsellRef.current
    if (!el || typeof IntersectionObserver === "undefined") return
    const watch = new IntersectionObserver(([entry]) => setUpsellInView(entry.isIntersecting), { threshold: 0.25 })
    watch.observe(el)
    return () => watch.disconnect()
  }, [upsellShown, editing])
  const office = selected?.pickup ? offices[selected.key] : null
  // whose pickup points an option's are, for a provider of the seller's own (its shops): the seller set up on its own, else the cart's one seller
  const sellerOf = (option) => editing || option?.sellerShipping?.[0]?.sellerId || null
  // pickup from the shop: with one shop it is that one, picked at once; with more, the picker opens
  const choosePickup = async (option) => {
    if (option.officeType !== "shop") return setPicker(true)
    const res = await ShippingOffices({ providerId: option.providerId, method: option.method, sellerId: sellerOf(option) }).catch(() => null)
    if (res?.offices?.length === 1) return pickOffice(res.offices[0], option.key)
    setPicker(true)
  }

  // the shop way chosen (or chosen for the buyer) with no shop yet: a seller with one shop has it picked
  useEffect(() => {
    if (selected?.officeType !== "shop" || offices[selected.key]) return
    let alive = true
    ShippingOffices({ providerId: selected.providerId, method: selected.method, sellerId: sellerOf(selected) })
      .then((res) => alive && res?.offices?.length === 1 && pickOffice(res.offices[0], selected.key))
      .catch(() => {})
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.key, editing])

  const pickOffice = async (picked, forKey) => {
    const key = forKey || selected.key
    setOffices((current) => ({ ...current, [key]: picked }))
    setPricing(true)
    // a seller set up on its own is priced alone
    const priced = await ShippingOptions({ ...request(), key, officeId: picked.id, ...(editing ? { sellerId: editing } : {}) }).catch(() => null)
    setPricing(false)
    if (priced?.options?.[0]) setOptions((list) => list.map((option) => (option.key === key ? priced.options[0] : option)))
  }

  // the couriers price in their own currency (the option says which); the
  // buyer reads it in the cart's currency
  const { currency, convert } = useCurrency()
  const inCart = (option) => convert(Math.round((option?.totalCost || 0) * 100), option?.currency) / 100
  const cost = inCart(selected)

  // what is sold before it is out ships once it is
  const presales = presaleLines(cart)
  const physicalLines = cart.filter((line) => isPhysicalProduct(lineProduct(line)))
  const presaleOnlyBy =
    presales.length && presales.length === physicalLines.length && presales.every((p) => p.presale.deliverBy)
      ? new Date(Math.max(...presales.map((p) => p.presale.deliverBy.getTime())))
      : null

  // each seller's way, as set: its option, and whether it is complete (an office picked for a pickup)
  const chosenOf = (seller) => seller.options.find((option) => option.key === choices[seller.sellerId]?.key) || null
  // when it arrives: the chosen courier's delivery time when the seller set its terms (the slowest of the
  // couriers chosen seller by seller), else the products' own days
  const courierDays = perSeller
    ? perSeller.reduce((max, seller) => {
        const own = chosenOf(seller)?.deliveryDays
        return own && (max === null || own > max) ? own : max
      }, null)
    : selected?.deliveryDays || null
  const days = courierDays ?? maxDeliveryDays(cart)
  const isSet = (seller) => {
    const option = chosenOf(seller)
    return !!option && !option.unavailable && (!option.pickup || !!choices[seller.sellerId]?.office)
  }
  const allSet = !!perSeller && perSeller.every(isSet)
  // the delivery chosen for a seller already costs nothing (its shop, a free way): its products below are a good deal,
  // not a way to free delivery
  const deliveryFree = (sellerId) => {
    if (!perSeller) return !!selected && !selected.unavailable && (selected.totalCost || 0) === 0
    const seller = perSeller.find((entry) => String(entry.sellerId) === String(sellerId))
    const option = seller && chosenOf(seller)
    return !!option && isSet(seller) && (option.totalCost || 0) === 0
  }
  const allFree = perSeller ? perSeller.every((seller) => deliveryFree(seller.sellerId)) : deliveryFree(null)

  // a seller's setup opened: its options as a one-seller checkout, the way set before selected
  const editSeller = (seller) => {
    const choice = choices[seller.sellerId]
    setOptions(seller.options)
    setOffices(sellerOffices[seller.sellerId] || (choice?.office ? { [choice.key]: choice.office } : {}))
    // as a one-seller checkout: a way already set, else one that needs nothing more — never a pickup whose
    // office is not picked (the buyer selects it and picks the office)
    const known = sellerOffices[seller.sellerId] || (choice?.office ? { [choice.key]: choice.office } : {})
    const ready = (option) => !option.unavailable && (!option.pickup || !!known[option.key])
    const usable = (key) => seller.options.some((option) => option.key === key && ready(option))
    setSelectedKey(usable(choice?.key) ? choice.key : seller.options.find(ready)?.key || null)
    setEditing(seller.sellerId)
  }
  // its way kept, back to the list of sellers
  const saveSeller = () => {
    if (!selected) return
    setPerSeller((sellers) => sellers.map((seller) => (seller.sellerId === editing ? { ...seller, options } : seller)))
    setChoices((current) => ({ ...current, [editing]: { key: selected.key, office: selected.pickup ? office : null } }))
    setSellerOffices((current) => ({ ...current, [editing]: offices }))
    setEditing(null)
    setOptions([])
  }
  // every seller set: the ways go on to the payment, their costs summed
  const continuePerSeller = () => {
    const deliveries = perSeller.map((seller) => {
      const option = chosenOf(seller)
      if (option?.officeType === "shop") rememberShop(seller.sellerId, choices[seller.sellerId]?.office)
      return {
        sellerId: seller.sellerId,
        sellerName: seller.sellerName,
        option: { key: option.key, provider: option.providerId, providerName: option.providerName, method: option.method, pickup: option.pickup, office: option.pickup ? choices[seller.sellerId]?.office || null : null, logo: option.carrier?.logo || null, label: option.label },
        shipping: { totalCost: option.totalCost, currency: option.currency, sellerShipping: option.sellerShipping },
      }
    })
    const total = deliveries.reduce((sum, entry) => sum + inCart(entry.shipping), 0)
    setValue(PAY_AT_SHOP_KEY, perSeller.every((seller) => chosenOf(seller)?.officeType === "shop"))
    store.setCartExtras(total > 0 ? [{ id: "shipping", label: t`Shipping`, amount: total, currency }] : [])
    track("Tracking_AddShippingInfo", cart.map((line) => ga4Item(line)), {
      shipping_tier: deliveries.map((entry) => [entry.option.providerName || entry.option.provider, entry.option.method].join(" · ")).join(" + "),
    })
    PubSub.publish("PURCHASE_FLOW_NEXT", {
      stepData: {
        perSeller: true,
        deliveries,
        option: null,
        offices: {},
        shipping: { totalCost: deliveries.reduce((sum, entry) => sum + (entry.shipping.totalCost || 0), 0), currency: deliveries[0]?.shipping.currency, sellerShipping: deliveries.flatMap((entry) => entry.shipping.sellerShipping || []) },
        days,
      },
    })
  }

  const onContinue = () => {
    if (perSeller) return editing ? saveSeller() : allSet ? continuePerSeller() : null
    if (!physical) {
      setValue(PAY_AT_SHOP_KEY, false)
      store.setCartExtras([])
      return PubSub.publish("PURCHASE_FLOW_NEXT", { stepData: { option: null, shipping: { totalCost: 0, currency }, days } })
    }
    if (!selected) return
    store.setCartExtras(cost > 0 ? [{ id: "shipping", label: t`Shipping`, amount: cost, currency }] : [])
    saveLocal({ providerId: selected.providerId, method: selected.method, office: office || null })
    if (selected.officeType === "shop") rememberShop(sellerOf(selected), office)
    setValue(PAY_AT_SHOP_KEY, selected.officeType === "shop")
    // how it travels (GA4 add_shipping_info): the courier and the way, what the cart comes to
    track("Tracking_AddShippingInfo", cart.map((line) => ga4Item(line)), {
      shipping_tier: [selected.providerName || selected.providerId, selected.method].filter(Boolean).join(" · "),
    })
    PubSub.publish("PURCHASE_FLOW_NEXT", {
      stepData: {
        // the courier's logo goes along: the address step shows it beside the office
        option: { key: selected.key, provider: selected.providerId, providerName: selected.providerName, method: selected.method, pickup: selected.pickup, office: office || null, logo: selected.carrier?.logo || null },
        offices,
        shipping: { totalCost: selected.totalCost, currency: selected.currency, sellerShipping: selected.sellerShipping, providerId: selected.providerId, method: selected.method },
        days,
      },
    })
  }
  continueRef.current = onContinue

  const optionReady = !pending && !pricing && !error && !!selected && !selected.unavailable && (!selected.pickup || !!office)
  // the list of sellers continues once every one is set; a seller's setup once its way is complete
  const ready = perSeller ? (editing ? optionReady : !pending && allSet) : !physical || optionReady
  useEffect(() => {
    if (!isActive) return
    PubSub.publish("PURCHASE_FLOW_UPDATE_BUTTON", { stepId: "delivery", canContinue: ready, loading: false, onContinue: () => continueRef.current?.() })
  }, [isActive, ready, editing])

  const UNAVAILABLE = {
    too_heavy: t`The parcel is too heavy for a locker`,
    not_in_country: t`Not available in this country`,
    not_for_these_products: t`Not available for these products`,
  }
  // why the cart has no way to go: a seller delivers none yet, one seller's products share none, or the sellers' do not
  const problemText = (problem) =>
    problem.reason === "not_to_country"
      ? problem.sellerName
        ? t("%seller% does not deliver to this country — remove its products from the cart to order the rest, or choose another address.", problem.sellerName)
        : t`A seller in the cart does not deliver to this country — remove its products, or choose another address.`
      : problem.reason === "no_shipping_provider"
      ? problem.sellerName
        ? t("%seller% does not deliver yet — remove its products from the cart to order the rest.", problem.sellerName)
        : t`A seller in the cart does not deliver yet — remove its products to order the rest.`
      : problem.reason === "no_common_method" && problem.sellerName
      ? t("Some products from %seller% cannot be delivered together — order them separately.", problem.sellerName)
      : t`Some products in the cart cannot be delivered together — order them separately.`

  if (pending) return <Spinner className="py-16" />

  // sellers with no courier in common: the list of them, each with its way or a button to set it
  if (perSeller && !editing) {
    return (
      <div className="min-h-full flex flex-col gap-4 px-4 py-6" data-testid="checkout-delivery-sellers">
        <div>
          <p className="m-0 text-[17px] font-bold">{t("You are ordering from %s sellers", perSeller.length)}</p>
          <p className="m-0 mt-1 text-[14px] text-text_secondary">{t`They deliver with different couriers, so each sends its own parcel. Set the delivery for each of them.`}</p>
        </div>
        <ul className="m-0 p-0 list-none flex flex-col gap-3">
          {perSeller.map((seller) => {
            const option = chosenOf(seller)
            const done = isSet(seller)
            const sellerOffice = choices[seller.sellerId]?.office
            const sellerCost = done ? inCart(option) : 0
            return (
              <li key={seller.sellerId} className={`flex items-center gap-3 rounded-lg border px-4 py-4 ${done ? "border-bordered" : "border-primary/50"}`} data-testid="delivery-seller" data-set={done ? "yes" : "no"}>
                {done ? <CheckCircleIcon className="w-6 h-6 shrink-0 text-success" data-testid="delivery-seller-done" /> : <span className="w-6 h-6 shrink-0 rounded-full border-2 border-bordered" />}
                <span className="flex-1 min-w-0">
                  <span className="block font-bold">{seller.sellerName || t`Seller`}</span>
                  {done ? (
                    <span className="block text-[14px] text-text_secondary">
                      {t(option.label)} · <span className="font-bold text-text">{sellerCost > 0 ? formatMoney(Math.round(sellerCost * 100), currency) : <FreeDeliveryChip label={t`Free`} />}</span>
                      {sellerOffice && <span className="block">{sellerOffice.name}</span>}
                      {!option.pickup && address && <span className="block">{[address.line1, address.city].filter(Boolean).join(", ")}</span>}
                    </span>
                  ) : (
                    <span className="block text-[14px] text-text_secondary">{t`Delivery not set yet`}</span>
                  )}
                </span>
                {done ? (
                  <button type="button" onClick={() => editSeller(seller)} className="shrink-0 font-bold text-primary hover:underline" data-testid="delivery-seller-change">
                    {t`Change`}
                  </button>
                ) : (
                  <Button variant="primary_s" className="shrink-0 !rounded-full" onClick={() => editSeller(seller)} data-testid="delivery-seller-set">
                    {t`Set delivery`}
                  </Button>
                )}
              </li>
            )
          })}
        </ul>
        {allSet && (
          <p className="m-0 rounded-md bg-success/10 px-3 py-2 text-[14px] font-semibold text-success" data-testid="delivery-sellers-all-set">
            {t`All delivery options are set — you may continue.`}
          </p>
        )}
        {/* what the parcels set so far come to: at the foot of the step, above its buttons — as the payment step's total */}
        {perSeller.some(isSet) && (
          <div
            className="sticky bottom-0 -mx-4 -mb-6 mt-auto min-h-[50px] flex items-center justify-end gap-2 px-4 py-2 bg-paper border-t-2 border-bordered z-[50]"
            style={{ boxShadow: "0 -4px 6px -1px rgba(0, 0, 0, 0.1)" }}
            data-testid="delivery-sellers-total"
          >
            <span className="text-[16px] font-semibold">{t`Delivery cost`}:</span>
            <span data-testid="delivery-sellers-total-amount">
              <PriceDisplay amount={Math.round(perSeller.filter(isSet).reduce((sum, seller) => sum + inCart(chosenOf(seller)), 0) * 100)} currency={currency} variant="medium" className="min-w-0" />
            </span>
          </div>
        )}
      </div>
    )
  }

  const editingSeller = perSeller && editing ? perSeller.find((seller) => seller.sellerId === editing) : null

  return (
    <div className="min-h-full flex flex-col gap-3 px-4 py-6" data-testid="checkout-delivery">
      {/* one seller set up on its own: which, and the way back to the list */}
      {editingSeller && (
        <div className="flex items-center gap-2 -mt-1 mb-1" data-testid="delivery-seller-editing">
          <button type="button" onClick={() => (setEditing(null), setOptions([]))} className="flex items-center gap-1 text-[14px] font-semibold text-text_secondary hover:text-text" aria-label={t`Back to the sellers`}>
            <ChevronLeftIcon className="w-4 h-4" />
            {t`Sellers`}
          </button>
          <span className="text-[15px] font-bold">· {t("Delivery from %s", editingSeller.sellerName || t`Seller`)}</span>
        </div>
      )}
      {options.map((option) => {
        const Icon = METHOD_ICONS[option.method] || TruckIcon
        const on = option.key === selectedKey
        const optionCost = inCart(option)
        const picked = offices[option.key]
        // the way picked: a thick primary outline (border + ring: 2px, nothing moves)
        return (
          <div key={option.key} className={`rounded-lg border ${on ? "border-primary ring-1 ring-primary" : "border-bordered"} ${option.unavailable ? "opacity-50" : ""}`}>
            <label className={`flex items-center gap-4 px-4 py-4 ${option.unavailable ? "cursor-not-allowed" : "cursor-pointer"}`} data-testid={`delivery-option-${option.method}`}>
              <input
                type="radio"
                name="delivery"
                checked={on}
                disabled={!!option.unavailable}
                onChange={() => {
                  setSelectedKey(option.key)
                  if (option.pickup && !offices[option.key]) choosePickup(option)
                }}
                className="w-5 h-5 accent-primary"
              />
              {option.carrier?.logo ? (
                <img src={cmsUrl(option.carrier.logo)} alt={option.carrier.name || option.providerName || ""} className="w-14 h-9 shrink-0 object-contain" />
              ) : (
                <Icon className="w-8 h-8 shrink-0" />
              )}
              <span className="flex-1 flex flex-col">
                <span className="font-bold">{t(option.label)}</span>
                {option.unavailable && <span className="text-[13px] text-error">{UNAVAILABLE[option.unavailable] || t`Not available for this cart`}</span>}
              </span>
              <span className="font-bold" data-testid={`shipping-cost-${option.method}`}>
                {on && pricing ? <Spinner size={20} className="w-auto py-0" /> : optionCost > 0 ? formatMoney(Math.round(optionCost * 100), currency) : <FreeDeliveryChip label={t`Free delivery`} />}
              </span>
            </label>

            {on && option.pickup && (
              // the office on the left, Change on the right — on a phone too
              <div className="flex flex-row items-center gap-3 px-4 pb-4 sm:pl-[60px]">
                {picked ? (
                  <span className="flex-1 flex items-start gap-2" data-testid="picked-office">
                    <MapPinIcon className="w-5 h-5 mt-0.5 shrink-0 text-primary" />
                    <span>
                      <span className="block font-semibold">{picked.name}</span>
                      {picked.address && <span className="block text-[14px] text-text_secondary">{officeAddress(picked)}</span>}
                      {option.officeType === "shop" && picked.hours && <span className="block text-[13px] text-text_secondary">{picked.hours}</span>}
                    </span>
                  </span>
                ) : (
                  <span className="flex-1 text-[14px] text-text_secondary">{option.officeType === "shop" ? t`Choose the shop you will collect the order from.` : option.officeType === "locker" ? t`Choose the locker you will collect the parcel from.` : t`Choose the office you will collect the parcel from.`}</span>
                )}
                <button type="button" onClick={() => setPicker(true)} className="shrink-0 font-bold text-primary hover:underline" data-testid="choose-office">
                  {picked ? t`Change` : option.officeType === "shop" ? t`Choose a shop` : option.officeType === "locker" ? t`Choose a locker` : t`Choose an office`}
                </button>
              </div>
            )}

            {on && option.sellerShipping?.length > 1 && (
              <ul className="m-0 px-4 pb-4 list-none flex flex-col gap-1 text-[13px] text-text_secondary" data-testid="seller-shipping">
                {/* each seller sends its own parcel: how many are coming */}
                <li className="font-semibold text-text" data-testid="seller-shipping-parcels">{t("%s parcels", option.sellerShipping.length)}</li>
                {/* each seller by name; its price only while one of them charges — all free, the option's own "Free" says it */}
                {option.sellerShipping.map((s, i) => (
                  <li key={i} className="flex justify-between">
                    <span>{s.sellerName || t`Seller`}</span>
                    {option.sellerShipping.some((x) => !(x.freeShipping || !x.shippingCost)) && (
                      <span>{s.freeShipping || !s.shippingCost ? <FreeDeliveryChip label={t`Free`} /> : formatMoney(convert(Math.round(s.shippingCost * 100), s.currency), currency)}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )
      })}
      {/* nothing could be loaded: in the middle of the step, with a way to try again (a connection
          lost says so, not "Network Error"); a failure beside options already shown, in one line */}
      {error && !options.length && (
        <RetryPanel
          className="min-h-[320px]"
          error={error?.network ? "Network Error" : null}
          message={error?.network ? null : error}
          busy={pending}
          onRetry={() => {
            setError(null)
            setAttempt((n) => n + 1)
          }}
        />
      )}
      {error && options.length > 0 && <p className="m-0 text-[14px] text-error">{error?.network ? t`We could not reach the server` : error}</p>}
      {problems.length > 0 && (
        <div className="flex flex-col gap-1 rounded-md border border-error/40 bg-error/5 px-3 py-2 text-[14px] text-error" role="alert" data-testid="delivery-problems">
          {[...new Set(problems.map(problemText))].map((text) => (
            <span key={text}>{text}</span>
          ))}
        </div>
      )}

      {presales.length > 0 && (
        <div className="flex flex-col gap-1 rounded-md border border-info/40 bg-info/5 px-3 py-2 text-[14px]" data-testid="delivery-presale">
          <span className="font-semibold text-info">{t`Pre-sale`}</span>
          {presales.map(({ line, name, presale }) => (
            <span key={line.pid}>
              {t("%name% is not out yet: it is delivered by %date%, up to 3 days after its release on %release%", name, presaleDate(presale.deliverBy, language), presaleDate(presale.releaseDate, language))}
            </span>
          ))}
        </div>
      )}

      {/* the products below out of sight: a chip floating over the foot of the step, as the cart's "Get a
          good deal" — a click scrolls them into view */}
      {!editingSeller && upsellShown && !upsellInView && (
        <div className="sticky bottom-[84px] z-[51] -mb-[30px] h-[30px] flex justify-center pointer-events-none" data-testid="delivery-upsell-jump-row">
          <button
            type="button"
            onClick={() => {
              // the step's scrolling box (the nearest that scrolls) to its very end
              let box = upsellRef.current?.parentElement
              while (box && !(box.scrollHeight > box.clientHeight && /(auto|scroll)/.test(getComputedStyle(box).overflowY))) box = box.parentElement
              if (box) box.scrollTo({ top: box.scrollHeight, behavior: "smooth" })
              else window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" })
            }}
            className="pointer-events-auto inline-flex items-center gap-1.5 h-[30px] rounded bg-primary_hov px-3 text-sm font-condensed font-medium uppercase tracking-wide text-white shadow-md hover:brightness-110 transition"
            data-testid="delivery-upsell-jump"
          >
            <CursorArrowRaysIcon className="w-4 h-4 stroke-2" />
            {allFree ? t`Get a good deal` : t`Free delivery`}
          </button>
        </div>
      )}
      {/* the foot of the step: the products that bring a seller to free delivery, then when it arrives */}
      <div className="mt-auto flex flex-col">
      {!editingSeller && (
        <div ref={upsellRef}>
          <DeliveryUpsell cart={cart} className="pt-5 pb-4" onShown={setUpsellShown} isFree={deliveryFree} />
        </div>
      )}
      </div>
      {/* when it arrives, once for the chosen way: at the foot of the step, as the payment step shows its
          total — a child of the step itself, so it stays at the bottom however far the step scrolls */}
      {days != null && selected && !selected.unavailable && (
        <div
          className="sticky bottom-0 -mx-4 -mb-6 min-h-[50px] flex items-center justify-between gap-3 px-4 py-2 bg-paper border-t-2 border-bordered z-[50]"
          style={{ boxShadow: "0 -4px 6px -1px rgba(0, 0, 0, 0.1)" }}
          data-testid="expected-delivery"
        >
          {/* when it arrives on the left; the total with this delivery on the right, as the payment step shows it */}
          <span className="flex flex-col min-w-0">
            <span className="text-[13px] text-text_secondary leading-tight whitespace-nowrap">{t`Expected delivery`}:</span>
            <span className="text-[15px] font-bold leading-tight whitespace-nowrap">
              {/* a cart of pre-sales only arrives with the last of them; one mixed with the
                  rest says when the rest does (the pre-sale note above tells the others) */}
              {presaleOnlyBy
                ? presaleOnlyBy.toLocaleDateString(language || undefined, { weekday: "short", day: "numeric", month: "long" })
                : expectedDelivery(days, language, { weekday: "short", day: "numeric", month: "long" })}
            </span>
          </span>
          <span className="flex flex-col items-end shrink-0">
            <span className="flex items-center gap-2">
              <span className="text-[16px] font-semibold">{t`Total`}:</span>
              <span data-testid="delivery-total">
                <PriceDisplay amount={cartSubtotal(cart, convert) + Math.round(cost * 100)} currency={currency} variant="medium" className="min-w-0" />
              </span>
            </span>
          </span>
        </div>
      )}

      <OfficePicker
        isOpen={picker && !!selected?.pickup}
        onClose={() => setPicker(false)}
        option={selected && { ...selected, sellerId: sellerOf(selected) }}
        city={address?.city}
        language={language}
        selected={office}
        onPick={pickOffice}
      />
    </div>
  )
}
